import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useRestaurantContext } from '@/contexts/RestaurantContext'
import { useRestaurants } from '@/hooks/useRestaurants'
import { Header } from '@/components/layout/Header'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { OrderStatsCard } from '@/components/analytics/OrderStatsCard'
import { DateSelector } from '@/components/analytics/DateSelector'
import { OrdersTable } from '@/components/analytics/OrdersTable'
import { InventoryInsights } from '@/components/analytics/InventoryInsights'
import { InventoryEvolutionChart } from '@/components/analytics/InventoryEvolutionChart'
import { ServerOrdersChart } from '@/components/analytics/ServerOrdersChart'
import { MenuPopularityChart } from '@/components/analytics/MenuPopularityChart'
import { Badge } from '@/components/ui/badge'
import { useDailyOrderStats, useDateRangeOrderStats, useDetailedOrders } from '@/hooks/useOrderStats'
import { ShoppingCart, DollarSign, TrendingUp } from 'lucide-react'
import { normalizeDateToMidnight, formatPrice } from '@/lib/utils'
import { supabase } from '@/lib/supabase'

export function AdminDashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { selectedRestaurantId, setSelectedRestaurantId } = useRestaurantContext()
  const { data: restaurants, isLoading } = useRestaurants()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly' | 'yearly' | 'all'>('daily')
  const pageSize = 10
  // Track if user is authenticated and their type
  const [authState, setAuthState] = React.useState<{
    isChecked: boolean
    isAuthenticated: boolean
    userType: string | null
  }>({ isChecked: false, isAuthenticated: false, userType: null })

  // Check user type for proper restaurant welcome redirect
  React.useEffect(() => {
    const checkUserType = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.user) {
        // Not authenticated - ProtectedRoute will handle showing login
        setAuthState({ isChecked: true, isAuthenticated: false, userType: null })
        return
      }

      const type = session.user.user_metadata?.['user_type'] || null
      setAuthState({ isChecked: true, isAuthenticated: true, userType: type })
    }
    checkUserType()
  }, [navigate])

  // Note: ProtectedRoute will handle authentication
  // Only redirect to welcome if authenticated admin with no restaurant
  React.useEffect(() => {
    // Wait for auth check to complete
    if (!authState.isChecked) {
      return
    }

    // Don't redirect unauthenticated users - ProtectedRoute will show login
    if (!authState.isAuthenticated) {
      return
    }

    // Skip for assistants - they don't need to go to welcome page
    if (authState.userType === 'assistant') {
      return
    }

    // Only redirect authenticated admin users with no restaurants to welcome
    if (!isLoading && Array.isArray(restaurants) && restaurants.length === 0) {
      console.log('[AdminDashboard] No restaurants found, redirecting to welcome')
      navigate({ to: '/welcome' })
    }
  }, [isLoading, restaurants, navigate, authState])

  // Auto-select first restaurant if none selected but restaurants exist
  React.useEffect(() => {
    if (!isLoading && restaurants && restaurants.length > 0 && !selectedRestaurantId) {
      setSelectedRestaurantId(restaurants[0]!.id)
    }
  }, [isLoading, restaurants, selectedRestaurantId, setSelectedRestaurantId])

  // Calculate date range based on period
  const getDateRange = () => {
    // Normalize selected date to midnight in local timezone
    const normalizedSelected = normalizeDateToMidnight(selectedDate)
    const end = new Date(normalizedSelected)
    end.setHours(23, 59, 59, 999) // End of selected day
    const start = new Date(normalizedSelected)

    if (period === 'daily') {
      // Same day for daily - start at midnight, end at end of day
      start.setHours(0, 0, 0, 0)
      return { start, end }
    } else if (period === 'weekly') {
      // Last 7 days from selected date (including selected date)
      start.setDate(start.getDate() - 6) // Go back 6 days (7 days total including today)
      start.setHours(0, 0, 0, 0)
      return { start, end }
    } else if (period === 'monthly') {
      // Last 30 days from selected date (including selected date)
      start.setDate(start.getDate() - 29) // Go back 29 days (30 days total including today)
      start.setHours(0, 0, 0, 0)
      return { start, end }
    } else if (period === 'yearly') {
      // Last 12 months (365 days) from selected date
      start.setDate(start.getDate() - 364) // Go back 364 days (365 days total including today)
      start.setHours(0, 0, 0, 0)
      return { start, end }
    } else {
      // 'all' - Very far back date to get all orders
      start.setFullYear(2000, 0, 1)
      start.setHours(0, 0, 0, 0)
      return { start, end }
    }
  }

  const dateRange = getDateRange()

  // Normalize dates for stats queries
  const normalizedSelectedDate = normalizeDateToMidnight(selectedDate)

  // Fetch stats based on selected period
  const { data: dailyStats } = useDailyOrderStats(normalizedSelectedDate, selectedRestaurantId)
  const { data: dateRangeStats } = useDateRangeOrderStats(
    selectedRestaurantId,
    dateRange.start,
    dateRange.end
  )
  const { data: detailedOrdersData, isLoading: isLoadingDetailedOrders } = useDetailedOrders(
    selectedRestaurantId,
    dateRange.start,
    dateRange.end
  )

  // Select stats based on period
  const stats = period === 'daily' ? dailyStats : dateRangeStats


  // Show loading while restaurants are being fetched
  if (isLoading) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="dashboard">
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    )
  }

  // Show loading while redirecting if no restaurants exist
  if (!restaurants || restaurants.length === 0) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="dashboard">
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    )
  }

  // If restaurants exist but none selected, show loading (useEffect will set it)
  if (!selectedRestaurantId) {
    return (
      <ProtectedRoute requiredRole="admin" requiredPermission="dashboard">
        <LoadingSpinner fullScreen />
      </ProtectedRoute>
    )
  }

  // Show main dashboard with header and content
  return (
    <ProtectedRoute requiredRole="admin" requiredPermission="dashboard">
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">
              {t('analytics.dashboard')}
            </h1>
            <p className="text-muted-foreground">
              {t('analytics.manageOperations')}
            </p>
          </div>

          {/* Period Selector and Date Picker */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Badge
                variant={period === 'daily' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setPeriod('daily')
                  setSelectedDate(new Date()) // Reset to today
                }}
              >
                {t('analytics.daily')}
              </Badge>
              <Badge
                variant={period === 'weekly' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setPeriod('weekly')
                  setSelectedDate(new Date()) // Reset to current week
                }}
              >
                {t('analytics.weekly')}
              </Badge>
              <Badge
                variant={period === 'monthly' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setPeriod('monthly')
                  setSelectedDate(new Date()) // Reset to current month
                }}
              >
                {t('analytics.monthly')}
              </Badge>
              <Badge
                variant={period === 'yearly' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setPeriod('yearly')
                  setSelectedDate(new Date()) // Reset to today
                }}
              >
                {t('analytics.yearly')}
              </Badge>
              <Badge
                variant={period === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => {
                  setPeriod('all')
                  setSelectedDate(new Date()) // Reset to today
                }}
              >
                {t('analytics.all')}
              </Badge>
            </div>
            <DateSelector
              date={selectedDate}
              onDateChange={setSelectedDate}
            />
          </div>

          {/* Statistics Cards */}
          <div className="grid gap-4 md:grid-cols-3 mb-8">
            <OrderStatsCard
              title={t('analytics.totalOrders')}
              value={stats?.totalOrders || 0}
              icon={ShoppingCart}
              iconColor="text-blue-600"
              subtitle={`${stats?.completedOrders || 0} ${t('analytics.completedOrders')}`}
            />
            <OrderStatsCard
              title={t('analytics.totalRevenue')}
              value={`${formatPrice(stats?.totalRevenue)} DH`}
              icon={DollarSign}
              iconColor="text-green-600"
            />
            <OrderStatsCard
              title={t('analytics.averageOrder')}
              value={`${formatPrice(stats?.averageOrder)} DH`}
              icon={TrendingUp}
              iconColor="text-purple-600"
            />
          </div>

          {/* Analytics Charts */}
          <div className="grid gap-4 md:grid-cols-2 mb-8">
            <ServerOrdersChart
              restaurantId={selectedRestaurantId}
              startDate={dateRange.start}
              endDate={dateRange.end}
            />
            <MenuPopularityChart
              restaurantId={selectedRestaurantId}
              startDate={dateRange.start}
              endDate={dateRange.end}
            />
          </div>

          {/* Detailed Orders Table */}
          <div className="mb-8">
            <OrdersTable
              orders={detailedOrdersData?.orders || []}
              pageSize={pageSize}
              isLoading={isLoadingDetailedOrders}
            />
          </div>

          {/* Inventory Insights Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">{t('inventory.insights.title')}</h2>
            <InventoryInsights restaurantId={selectedRestaurantId} />
          </div>

          {/* Inventory Evolution Chart */}
          <div className="mb-8">
            <InventoryEvolutionChart restaurantId={selectedRestaurantId} />
          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
